import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import {
  DashboardFilterName,
  DashboardFilterType,
  DashboardInstallationsStatusEnum,
} from '@vi-iot/client';

import { DashboardFilter, KanbanBoardHelperService, KanbanBoardStore } from '@store';

import { KanbanComplexFilterList } from '../kanban-installation-list-header.helper';
import { CommonStandaloneModule } from '../../../../shared/common-standalone.module';
import { DashboardFilterTranslationPipe } from '../../pipes/dashboard-filter-translation/dashboard-filter-translation.pipe';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Params } from '@angular/router';
import { RoutingService } from '@services';
import { DashboardFilterListHelper } from './dashboard-filter-list.helper';

@Component({
  selector: 'app-dashboard-filter-list',
  standalone: true,
  imports: [
    CommonStandaloneModule,
    MatFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    DashboardFilterTranslationPipe,
    MatButtonModule,
    MatIconModule,
  ],
  templateUrl: './dashboard-filter-list.component.html',
  styleUrls: ['./dashboard-filter-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardFilterListComponent implements OnInit, OnDestroy {
  @ViewChild('matSelect', { read: MatSelect })
  matSelect: MatSelect;

  readonly filterType = DashboardFilterType;

  @Input() buttonView: boolean;

  selectedFilters: FormControl<DashboardFilter[]> = new FormControl();

  currentlySelectedFilters: DashboardFilter[];
  filters$: Observable<KanbanComplexFilterList[]>;

  private ngUnsubscribe$: Subject<void> = new Subject();

  constructor(
    private kanbanBoardStore: KanbanBoardStore,
    private kanbanBoardHelperService: KanbanBoardHelperService,
    private cdRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private routingService: RoutingService
  ) {}

  ngOnInit(): void {
    this.kanbanBoardStore.fetchFilters();
    this.readyQueryParams();
    this.handleFilterChanges();
    this.assignFiltersStream();
  }

  // Select filters basing on url query parameter
  private readyQueryParams(): void {
    // Postpone until filters are present
    this.kanbanBoardStore.filters$
      .pipe(
        filter((value) => value.length > 0),
        take(1),
        withLatestFrom(this.activatedRoute.queryParams),
        takeUntil(this.ngUnsubscribe$)
      )
      .subscribe(([filters, queryParamsResponse]) => {
        const queryParams = Object.entries(queryParamsResponse);

        const isNavigatedFromPartnerPortal =
          queryParamsResponse[DashboardFilterListHelper.partnerPortalQueryParamKey] ===
          DashboardFilterListHelper.partnerPortalQueryParamValue;

        if (isNavigatedFromPartnerPortal) {
          return this.applyPartnerPortalFilter(queryParamsResponse, filters);
        }

        if (queryParams.length >= 0) {
          const filtersToAdd: DashboardFilter[] = [];
          queryParams.forEach(([key, value]) => {
            // Check if parameter consist of single or multiple values
            let multipleValues: string[] = [];

            const isSingleStirngValue = !value.includes(';');
            if (!isSingleStirngValue) {
              multipleValues = value.split(';');
            }

            const filter = {
              filterName: key,
              value,
            } as DashboardFilter;

            // If only one value is present - add it
            if (isSingleStirngValue) {
              // Check if query param input is correct
              const matchingFilter = this.findMatchingFilter(key, value, filters);
              if (!matchingFilter) {
                return;
              }

              filtersToAdd.push({ ...filter, type: matchingFilter.type });
            } else {
              // Iterate over multiple values
              multipleValues.forEach((iterationValue) => {
                // Check if query param input is correct when multiple values are passed
                const matchingFilter = this.findMatchingFilter(key, iterationValue, filters);

                if (!matchingFilter) {
                  return;
                }

                filtersToAdd.push({
                  ...filter,
                  value: iterationValue,
                  type: matchingFilter.type,
                });
              });
            }
          });

          const queryParamsKeys = Object.keys(queryParamsResponse);
          if (queryParamsKeys.length > 0) {
            this.routingService.removeQueryParams(queryParamsKeys);
          }

          this.applyFilters(filtersToAdd);
        }
      });
  }

  private applyPartnerPortalFilter(queryParams: Params, allFilters: DashboardFilter[]): void {
    const filtersToAdd: DashboardFilter[] = [];
    switch (queryParams.filter) {
      case DashboardFilterListHelper.PartnerPortalQueryParam.working:
        filtersToAdd.push(
          this.findMatchingFilter(
            DashboardFilterName.AggregatedStatus,
            DashboardInstallationsStatusEnum.WorksProperly,
            allFilters
          )
        );
        break;
      case DashboardFilterListHelper.PartnerPortalQueryParam.error:
        filtersToAdd.push(
          this.findMatchingFilter(
            DashboardFilterName.AggregatedStatus,
            DashboardInstallationsStatusEnum.Error,
            allFilters
          )
        );
        break;
      case DashboardFilterListHelper.PartnerPortalQueryParam.maintenance:
        filtersToAdd.push(
          this.findMatchingFilter(
            DashboardFilterName.AggregatedStatus,
            DashboardInstallationsStatusEnum.Maintenance,
            allFilters
          )
        );
        break;
      case DashboardFilterListHelper.PartnerPortalQueryParam.offline:
        filtersToAdd.push(
          this.findMatchingFilter(
            DashboardFilterName.AggregatedStatus,
            DashboardInstallationsStatusEnum.Offline,
            allFilters
          )
        );
        break;
    }

    const queryParamsKeys = Object.keys(queryParams);
    this.routingService.removeQueryParams(queryParamsKeys);
    this.applyFilters(filtersToAdd);
  }

  private findMatchingFilter(
    queryParamFilterKey: string,
    queryParamFilterValue: string,
    storeFilters: DashboardFilter[]
  ): DashboardFilter {
    return storeFilters.find(
      (filter) =>
        filter.filterName === queryParamFilterKey && filter.value === queryParamFilterValue
    );
  }

  private handleFilterChanges(): void {
    // Verify if filters selected after timeout are different
    this.kanbanBoardStore.selectedFilters$.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((res) => {
      this.currentlySelectedFilters = res;
      this.selectedFilters.setValue(this.currentlySelectedFilters, { emitEvent: false });
    });

    // Select/deselect filter in dropdown after clicking a filter button
    this.kanbanBoardHelperService
      .getSelectedFiltersOnFilterButtonClick()
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((selectedFilters) => {
        this.kanbanBoardHelperService.filtersSelectedInDropdown$.next(selectedFilters);
        this.selectedFilters.setValue(selectedFilters, { emitEvent: false });
        this.cdRef.detectChanges();
      });

    this.selectedFilters.valueChanges
      .pipe(distinctUntilChanged(isEqual), takeUntil(this.ngUnsubscribe$))
      .subscribe((filters) => {
        this.applyFilters(filters);
      });
  }

  private assignFiltersStream(): void {
    this.filters$ = this.kanbanBoardStore.filters$.pipe(
      map((filters) => {
        const organizedFilters: KanbanComplexFilterList[] = [];
        filters.forEach((filter) => {
          if (filter.type === DashboardFilterType.Toggle) {
            organizedFilters.push({
              filterName: filter.filterName,
              filters: [filter],
              type: DashboardFilterType.Toggle,
            });
          }

          if (filter.type === DashboardFilterType.Complex) {
            const matchingEntry = organizedFilters.find(
              (el) => el.filterName === filter.filterName
            );
            if (matchingEntry) {
              matchingEntry.filters.push(filter);
            } else {
              organizedFilters.push({
                filterName: filter.filterName,
                filters: [filter],
                type: DashboardFilterType.Complex,
              });
            }
          }
        });

        return organizedFilters;
      })
    );
  }

  compareFilterOptions(optionFilter: DashboardFilter, selectedFilter: DashboardFilter): boolean {
    return (
      optionFilter &&
      selectedFilter &&
      optionFilter.filterName === selectedFilter.filterName &&
      optionFilter.value === selectedFilter.value
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
    this.kanbanBoardHelperService.filtersSelectedInDropdown$.next([]);
  }

  private applyFilters(filters: DashboardFilter[]): void {
    this.kanbanBoardHelperService.filtersSelectedInDropdown$.next(filters);
    this.kanbanBoardStore.applyDashboardFilter();
  }
}
